import type { GetterTree, ActionTree, MutationTree } from "vuex";
import type { IState } from "@/store";
import type { CalendarSpec, MomentInput } from "moment";
import moment from "moment";
import i18n from "@/i18n";
import _ from "@/boot/lodash";

export interface IUtilsTimeState {
  now: Date;
}

const initialState: IUtilsTimeState = {
  now: new Date()
};

const getters: GetterTree<IUtilsTimeState, IState> = {
  hasTime:
    () =>
    (dateTime: string): boolean => {
      return typeof dateTime === "string"
        ? !!dateTime.match(/\s\d{2}:\d{2}:\d{2}?$/)
        : true;
    },
  /**
   * @name adaptiveDatetimeBindings
   * @returns Datetime bindings that will show relative time for today, but
   * an absolute date for other days. If the date is in the current year, the
   * year is omitted from the absolute date.
   */
  adaptiveDatetimeBindings:
    state =>
    (datetime: string, { omitTime = false } = {}) => {
      const localDatetime = moment.utc(datetime).local();
      const isSameDay = localDatetime.isSame(state.now, "day");
      const isSameYear = localDatetime.isSame(state.now, "year");
      const datetimeMs = new Date(datetime).getTime(); // This returns the datetime in milliseconds
      const currentMs = state.now.getTime(); // This returns the current time in milliseconds

      const absoluteFormat = i18n.t(
        isSameYear
          ? omitTime
            ? "_datetime.date.omit_year"
            : "_datetime.datetime.omit_year"
          : omitTime
          ? "_datetime.date.default"
          : "_datetime.datetime.default"
      );

      return {
        // If same day, add a `key` prop to force a re-render every minute
        key: isSameDay ? [datetimeMs, currentMs].join("-") : datetimeMs,
        datetime,
        formatFunction: "calendar",
        formatFunctionParams: {
          sameDay: (now: MomentInput) => `[${localDatetime.from(now)}]`,
          lastDay: absoluteFormat,
          lastWeek: absoluteFormat,
          sameElse: absoluteFormat
        }
      };
    },
  formatFunctionCalendar:
    (state, getters) =>
    (datetime: string, spec: CalendarSpec = {}): CalendarSpec => {
      const hasTime = getters["hasTime"](datetime);
      const mDate = moment.utc(datetime).local();

      const formatFunction: CalendarSpec = _.merge(
        {
          sameDay: (now: MomentInput) => {
            return hasTime
              ? `[${mDate.from(now)}]`
              : i18n.t("_datetime.calendar.sameDay").toString();
          },
          nextDay: i18n.t("_datetime.calendar.nextDay").toString(),
          nextWeek: i18n.t("_datetime.calendar.nextWeek").toString(),
          lastDay: (now: MomentInput) => {
            return hasTime
              ? `[${mDate.from(now)}]`
              : i18n.t("_datetime.calendar.lastDay").toString();
          },
          lastWeek: i18n.t("_datetime.calendar.lastWeek").toString(),
          sameElse: () => {
            return hasTime
              ? (i18n.t("_datetime.datetime.with_seconds") as string)
              : (i18n.t("_datetime.date.default") as string);
          }
        },
        spec
      );
      return formatFunction;
    },
  formatCalendar:
    (state, getters) =>
    (dateTime: string, spec: CalendarSpec = {}): string => {
      const formatFunction = getters["formatFunctionCalendar"](dateTime, spec);
      const momentDate = moment.utc(dateTime).local();
      return momentDate.calendar(formatFunction);
    }
};
const actions: ActionTree<IUtilsTimeState, IState> = {
  start: ({ commit }) => {
    setInterval(() => {
      commit("update");
    }, 1000 * 60);
  }
};

const mutations: MutationTree<IUtilsTimeState> = {
  update: state => {
    state.now = new Date();
  }
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations
};
